import React, { useState } from 'react';
import apiClient from '../../apiClient';
import './BulkPriceUpdateModal.css';

function BulkPriceUpdateModal({ plans, onClose, onUpdate }) {
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [percentageIncrease, setPercentageIncrease] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handlePlanSelection = (planId) => {
    setSelectedPlans(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      } else {
        return [...prev, planId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPlans.length === plans.length) {
      setSelectedPlans([]);
    } else {
      setSelectedPlans(plans.map(p => p.id));
    }
  };

  const calculatePreview = () => {
    if (!percentageIncrease || selectedPlans.length === 0) {
      setPreview(null);
      return;
    }

    const percentage = parseFloat(percentageIncrease);
    const multiplier = 1 + (percentage / 100);
    
    const previewData = plans
      .filter(plan => selectedPlans.includes(plan.id))
      .map(plan => ({
        id: plan.id,
        name: plan.name,
        current_monthly: plan.price_monthly,
        new_monthly: (plan.price_monthly * multiplier).toFixed(2),
        current_annual: plan.price_annual,
        new_annual: (plan.price_annual * multiplier).toFixed(2)
      }));
    
    setPreview(previewData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPlans.length === 0) {
      setError('Please select at least one plan');
      return;
    }
    
    if (!percentageIncrease || parseFloat(percentageIncrease) <= 0) {
      setError('Please enter a valid percentage increase');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await apiClient.post('/admin/subscription-plans/bulk-price-update', {
        plan_ids: selectedPlans,
        percentage_increase: parseFloat(percentageIncrease)
      });

      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update prices');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-update-modal">
        <div className="modal-header">
          <h2>Bulk Price Update</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3>Select Plans to Update</h3>
            
            <div className="select-all">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedPlans.length === plans.length}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
              </label>
            </div>

            <div className="plans-list">
              {plans.map(plan => (
                <label key={plan.id} className="plan-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedPlans.includes(plan.id)}
                    onChange={() => handlePlanSelection(plan.id)}
                  />
                  <div className="plan-info">
                    <span className="plan-name">{plan.name}</span>
                    <span className="plan-price">
                      {formatCurrency(plan.price_monthly)}/mo
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Price Adjustment</h3>
            
            <div className="form-group">
              <label htmlFor="percentage">Percentage Increase (%)</label>
              <input
                type="number"
                id="percentage"
                value={percentageIncrease}
                onChange={(e) => {
                  setPercentageIncrease(e.target.value);
                  setPreview(null);
                }}
                step="0.1"
                min="0.1"
                max="100"
                placeholder="e.g., 10"
              />
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={calculatePreview}
              disabled={!percentageIncrease || selectedPlans.length === 0}
            >
              Preview Changes
            </button>
          </div>

          {preview && (
            <div className="form-section">
              <h3>Preview</h3>
              <div className="preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Current Monthly</th>
                      <th>New Monthly</th>
                      <th>Current Annual</th>
                      <th>New Annual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map(item => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{formatCurrency(item.current_monthly)}</td>
                        <td className="new-price">
                          {formatCurrency(item.new_monthly)}
                        </td>
                        <td>{formatCurrency(item.current_annual)}</td>
                        <td className="new-price">
                          {formatCurrency(item.new_annual)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={loading || selectedPlans.length === 0 || !percentageIncrease}
            >
              {loading ? 'Updating...' : 'Update Prices'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BulkPriceUpdateModal;